import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Paper, Stack, TextField, Typography,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

import { cascadeDeleteIpam, disableIpam, getIpamActiveTeardown } from '../../../../services/ipamApi';
import { POLL_INTERVAL_MS } from '../pollInterval';
import {
  CK, FONT_BODY, FONT_MONO, FONT_TITLE, actionRowSx, dialogProps, dialogTitleSx,
  insetPanelSx, sectionCardSx, sectionHeadingSx, statusChipSx,
} from '../../portalStyles';
import {
  IPAM_NO_VPC_DELETED,
  IPAM_ADOPTED_TEARDOWN_SUMMARY,
  IPAM_HISTORY_DESTROYED,
  IPAM_NEVER_DELETES_YOUR_RESOURCES,
  IPAM_ALLOCATION_RELEASE_DELAY,
  IPAM_FORCE_DELETE_COPY,
  IPAM_CASCADE_NOT_YET_REASON,
  IPAM_CASCADE_UNREADABLE_REASON,
  IPAM_TEARDOWN_AWAITING_CASCADE,
  ipamBillingStops,
  ipamDestructiveConfirmLabel,
  ipamHistoryDestroyed,
  // The re-export block below forwards this name to other modules; a re-export
  // does NOT put the binding in local scope, so the panel needs its own import.
  ipamPreviewBillingLine,
  ipamResidueMeterVerdict,
  ipamTeardownBlockedCopy,
} from './ipamCopy';

// IpamBlockedPanel — the `blocked` teardown outcome (spec §9.4), plus the two
// pieces of destruction copy every other teardown screen has to agree with.
//
// WHY THE SHARED COPY AND THE CONFIRM DIALOG LIVE HERE, NOT ON THE SETTINGS PAGE.
// `ipamPricing.js` exists because "a price that disagrees with itself between the
// wizard and settings is a support ticket at best". The same argument applies
// with more force to destruction copy: this panel and IpamSettingsPage's danger
// zone are the two screens on which an operator authorises an irreversible,
// money-bearing action, and two spellings of "what teardown destroys" is how one
// of them quietly stops being true. One module, imported twice, enforces that
// instead of restating it. IpamSettingsPage imports these constants and this
// dialog; nothing here imports the settings page, so there is no cycle.
//
// The AWS behaviour is what makes this panel's advice real rather than
// reassuring. `ReleaseIpamPoolAllocation` genuinely refuses a resource-backed
// allocation — AWS's own instruction is "remove the resource using the
// corresponding cidr and IPAM will automatically release the allocation" — and
// after the VPC is deleted the allocation persists as a ghost for several minutes
// during which release still refuses. So "wait a few minutes and retry" is real
// advice, and `DeleteIpam --cascade` really is the reliable escape: it was
// measured clearing a `failed-deprovision` CIDR plus a ghost allocation in ~45 s.

// Slate / blue-grey 700. Per portal convention the accent hex is a local const
// per page file rather than a shared export.
const IPAM_COLOR = '#455A64';
const IPAM_COLOR_DARK = '#31434B';

const bodyTextSx = { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body };
const hintTextSx = { fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted };
const monoTextSx = {
  fontFamily: FONT_MONO,
  fontSize: '0.8125rem',
  color: CK.navy,
  wordBreak: 'break-word',
};

const ipamButtonSx = {
  backgroundColor: IPAM_COLOR,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: IPAM_COLOR_DARK },
  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
};

const plainButtonSx = { fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' };

// ---------------------------------------------------------------------------
// The copy both teardown screens must show identically
// ---------------------------------------------------------------------------

// The verbatim teardown copy now lives in ipamCopy.js, so the four screens that
// state it cannot drift apart. Re-exported here because this panel was its
// original home and several callers still import it from this module; both paths
// resolve to the one definition.
export {
  IPAM_NO_VPC_DELETED,
  IPAM_ADOPTED_TEARDOWN_SUMMARY,
  IPAM_HISTORY_DESTROYED,
  IPAM_BILLING_STOPS,
  IPAM_RESIDUE_METER_DEFERRED,
  IPAM_RESIDUE_UNFINISHED_TITLE,
  ipamBillingStops,
  ipamDestructiveConfirmLabel,
  ipamHistoryDestroyed,
  ipamPreviewBillingLine,
  ipamTeardownBlockedCopy,
  IPAM_STILL_BILLING,
  IPAM_NEVER_DELETES_YOUR_RESOURCES,
  IPAM_ALLOCATION_RELEASE_DELAY,
  IPAM_FORCE_DELETE_COPY,
  IPAM_CASCADE_NOT_YET_REASON,
  IPAM_CASCADE_UNREADABLE_REASON,
  IPAM_DESTRUCTIVE_CONFIRM_LABEL,
  IPAM_TEARDOWN_AWAITING_CASCADE,
  IPAM_TEARDOWN_BLOCKED,
} from './ipamCopy';

// Three consecutive failures before the poll gives up, matching the portal's
// house rule. One transient 502 must not blank a screen that is narrating a
// running meter.
const MAX_POLL_FAILURES = 3;

// ---------------------------------------------------------------------------
// Parsing the job's JSON columns
// ---------------------------------------------------------------------------

// parseIpamBlockers reads ipam_teardown_jobs.BlockersJSON, whose entries are
// {poolId, allocationId, cidr, resourceId, resourceType, resourceRegion,
// accountId}. It accepts the decoded array too, so a backend that starts
// returning a decoded field instead of the raw column needs no page edit.
//
// A parse failure yields [] rather than throwing: the promise, the instruction
// and the still-billing warning on this panel are all true whether or not the
// blocker list could be read, and an exception here would replace the whole
// screen with a stack trace at the exact moment the operator needs the advice.
export function parseIpamBlockers(source) {
  if (!source) return [];
  let raw = source;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch (_) {
      return [];
    }
  }
  if (!Array.isArray(raw)) return [];
  return raw.filter((b) => b && typeof b === 'object');
}

// groupIpamBlockersByPool turns the flat list into one group per pool, because
// the pool is the thing that cannot be deleted and the resources are the reasons.
// Blockers with no pool id are grouped under an empty key rather than dropped —
// a blocker Prism cannot attribute is still a blocker the customer must clear.
export function groupIpamBlockersByPool(blockers) {
  const byPool = new Map();
  blockers.forEach((b) => {
    const key = String(b.poolId || '');
    if (!byPool.has(key)) byPool.set(key, { poolId: key, cidrs: [], items: [] });
    const group = byPool.get(key);
    group.items.push(b);
    const cidr = String(b.cidr || '').trim();
    if (cidr && !group.cidrs.includes(cidr)) group.cidrs.push(cidr);
  });
  return [...byPool.values()];
}

// parseIpamAccountMap reads the keyed map[accountID]string that ErrorsJSON and
// ResidueJSON both carry. Two columns, one shape: merging them would make "did
// this teardown succeed?" unanswerable, so they are never merged here either.
export function parseIpamAccountMap(source) {
  if (!source) return {};
  let raw = source;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch (_) {
      return {};
    }
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  Object.keys(raw).forEach((k) => {
    if (typeof raw[k] === 'string') out[k] = raw[k];
  });
  return out;
}

// The config's error_message on a partial failure is the same map rendered as
// "account: message | account: message". Recovering the keys from it is the
// fallback for the case the job row is already terminal — /teardown/active
// answers null for a finished job, so on partial_failure the prose message is
// often the only copy of the map the page can reach.
//
// IT REFUSES TO GUESS. Every segment must start with a 12-digit account id (or
// the backend's literal `unattributed` key, chosen precisely because no account
// id can collide with it); one segment that does not match and the whole parse is
// abandoned, because half a list of affected accounts is worse than none.
export function parseIpamAccountMessage(message) {
  const text = String(message || '').trim();
  if (!text) return null;
  const parts = text.split(' | ');
  const out = {};
  for (let i = 0; i < parts.length; i += 1) {
    const sep = parts[i].indexOf(': ');
    if (sep <= 0) return null;
    const key = parts[i].slice(0, sep).trim();
    if (!/^\d{12}$/.test(key) && key !== 'unattributed') return null;
    out[key] = parts[i].slice(sep + 2).trim();
  }
  return Object.keys(out).length > 0 ? out : null;
}

// ---------------------------------------------------------------------------
// The type-the-customer-ID guard, shared by both destructive screens
// ---------------------------------------------------------------------------

/**
 * IpamDestructiveConfirmDialog — the type-the-customer-ID gate.
 *
 * THE TYPED VALUE IS CLEARED ON EVERY EXIT PATH, not just on Cancel. Left in
 * state, reopening the dialog arrives with the guard already satisfied and the
 * destructive button live on the first click — which is the whole thing the
 * guard exists to prevent. There are two clears and they are both deliberate:
 * `close()` covers Cancel and the backdrop, and the `open` effect covers every
 * other way the parent can take the dialog down (a successful dispatch, a
 * failed one, a mode change, an unmount-free re-render). A rule enforced on one
 * exit path is a rule that holds until someone adds a second path.
 */
export function IpamDestructiveConfirmDialog({
  open,
  customerId,
  title = 'Confirm teardown',
  intro,
  blockers = [],
  busy = false,
  // ADOPTION CHANGES WHAT THIS DIALOG IS AUTHORISING. Prism never deletes an
  // IPAM it adopted (services/ipam_teardown.go raises a `critical` residue row
  // saying so and sets StillBilling = true), so on an adopted IPAM the two
  // strongest sentences here — the history loss and the meter stopping — are both
  // false, and so is the confirm button's own label. Defaults to false: a caller
  // that does not pass it gets today's created-IPAM wording unchanged.
  adopted = false,
  confirmLabel,
  onClose,
  onConfirm,
}) {
  const [typed, setTyped] = useState('');

  const close = useCallback(() => {
    setTyped('');
    if (onClose) onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  return (
    <Dialog open={!!open} onClose={close} {...dialogProps}>
      <DialogTitle sx={dialogTitleSx}>{title}</DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {intro && <Box sx={{ mt: 1, mb: 2 }}>{intro}</Box>}

        {/* The three most consequential ledger lines, repeated here. The ledger
            is a scroll away by the time this dialog is open, and a confirmation
            that restates nothing asks the operator to remember what they read. */}
        <Box sx={{ ...insetPanelSx, mb: 2 }}>
          <Stack spacing={1}>
            <Typography sx={{ ...bodyTextSx, color: CK.navy, fontWeight: 600 }}>
              {IPAM_NO_VPC_DELETED}
            </Typography>
            <Typography
              sx={{ ...bodyTextSx, color: adopted ? CK.navy : CK.red, fontWeight: 600 }}
            >
              {ipamHistoryDestroyed(adopted)}
            </Typography>
            <Typography sx={{ ...bodyTextSx, ...(adopted ? { color: CK.red, fontWeight: 600 } : {}) }}>
              {ipamBillingStops(adopted)}
            </Typography>
            {adopted && (
              <Typography sx={{ ...bodyTextSx, color: CK.navy, fontWeight: 600 }}>
                {IPAM_ADOPTED_TEARDOWN_SUMMARY}
              </Typography>
            )}
          </Stack>
        </Box>

        {blockers.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ ...bodyTextSx, fontWeight: 600, color: CK.navy, mb: 0.5 }}>
              These resources block the ordered teardown:
            </Typography>
            <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, my: 0 }}>
              {blockers.map((b) => (
                <Typography
                  component="li"
                  key={`${b.poolId || 'pool'}-${b.allocationId || b.resourceId}`}
                  sx={monoTextSx}
                >
                  {blockerLine(b)}
                </Typography>
              ))}
            </Stack>
          </Box>
        )}

        <Typography sx={{ ...bodyTextSx, mt: 1 }}>
          Type the customer ID <b>{customerId}</b> to confirm:
        </Typography>
        <TextField
          fullWidth
          label="Customer ID"
          placeholder={customerId}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={close} sx={{ ...plainButtonSx, color: CK.body }}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={busy || !customerId || typed !== customerId}
          onClick={onConfirm}
          sx={plainButtonSx}
        >
          {confirmLabel || ipamDestructiveConfirmLabel(adopted)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// blockerLine renders one {resourceId, type, region, account} in the order §9.4
// states it. Every field is drawn even when empty, as an explicit em-dash: a
// blocker missing its account id must not look like a blocker in the host
// account.
function blockerLine(b) {
  const parts = [
    String(b.resourceType || '—'),
    String(b.resourceRegion || '—'),
    `account ${b.accountId || '—'}`,
  ];
  return `${b.resourceId || '—'} — ${parts.join(', ')}`;
}

// ---------------------------------------------------------------------------
// The residue banner — partial_failure, and it does not scroll away
// ---------------------------------------------------------------------------

/**
 * IpamResidueBanner — the persistent, NON-DISMISSIBLE banner for a TEARDOWN that
 * half-failed. There is deliberately no close affordance and no dismissed-state:
 * a warning that scrolls away is not a warning while a meter is running, and a
 * warning that can be closed is one the operator closes.
 *
 * IT IS NOT THE BANNER FOR EVERY `partial_failure`. Three unrelated code paths
 * write that status — the enable fan-out (ipam_service.go rollupConfigStatus),
 * the operating-region sync, and a teardown that ended with errors — and only
 * the third one means "a teardown did not finish". Rendering this on the other
 * two tells an operator whose accounts merely half-enrolled that their teardown
 * failed, and offers them a teardown as the remedy. THE HOST PAGE OWNS THAT
 * DISCRIMINATION and must not mount this banner without it.
 *
 * The keyed map[accountID]string reaches it three ways, in order of preference:
 *   `errors` (already decoded) → `job.errorsJson` → the config's error_message,
 * which the backend renders as "account: message | account: message". The third
 * exists because /teardown/active answers null once the job is terminal, and a
 * failed teardown's job IS terminal — so on the screen that most needs the map,
 * the prose message is often the only copy of it left.
 *
 * IT DOES NOT DISPATCH A TEARDOWN ITSELF. This banner used to call
 * `disableIpam('graceful')` straight from its action button: no dialog, no typed
 * customer ID, on the very page whose stage 3 demands one for exactly that call.
 * A banner is a warning, and a warning must not be a one-click route into an
 * irreversible, money-bearing action. The host passes `onRetry`, which is
 * expected to open its own confirmation; with no `onRetry` there is no button,
 * because a destructive control with no confirmation behind it is worse than no
 * control at all.
 *
 * Props: { job, config, errors, configMessage, totalAccounts, stillBilling,
 * busy, onRetry }. `totalAccounts` is the enrolled-account count the host page
 * already holds; when it is absent or nonsensical the "of {m}" clause is dropped
 * rather than filled with a number the banner made up.
 *
 * `stillBilling` is IpamTeardownPreview.StillBilling, passed straight down by the
 * host that already read the pre-flight. IT IS THE ONLY THING ON THIS BANNER
 * ALLOWED TO DECIDE THE METER — see ipamResidueMeterVerdict. `undefined` is a
 * third value and is spent as a hedge, never as a "no".
 *
 * `billingVerified` is IpamTeardownPreview.BillingVerified from THAT SAME
 * payload, and it decides nothing: it cannot turn a stopped meter into a running
 * one or the reverse. It only says whether the verdict rests on a current
 * observation, so that a stop resting on a ledger Prism has stopped refreshing
 * is not announced here as the money question being closed.
 */
export function IpamResidueBanner({
  job,
  config,
  errors,
  configMessage,
  totalAccounts,
  stillBilling,
  billingVerified,
  busy = false,
  onRetry,
}) {
  const message = configMessage || config?.errorMessage || '';

  const failures = useMemo(() => {
    const decoded = errors && typeof errors === 'object' && !Array.isArray(errors)
      ? errors
      : parseIpamAccountMap(errors || job?.errorsJson);
    if (Object.keys(decoded).length > 0) return decoded;
    return parseIpamAccountMessage(message) || {};
  }, [errors, job, message]);

  const accounts = Object.keys(failures).sort();
  const n = accounts.length;

  // MAY THIS BANNER ASSERT A LIVE METER?
  //
  // `n > 0 || !message` INFERRED A LIVE METER FROM THE SHAPE OF A STRING: a
  // per-account map was taken as proof the IPAM was still up. That is false. The
  // settle arm that writes a per-account map CO-OCCURS with a successful
  // DeleteIpam — a host-side step can fail into run.errs without aborting the
  // stage loop, and stage 9/10 checkpoint failures land AFTER the delete — so on
  // the danger tab this banner announced "still running and still billing" ten
  // lines above ipamPreviewBillingLine saying the pre-flight reports nothing is
  // billing. One screen, two opposite claims, both from Prism.
  //
  // IpamTeardownPreview.StillBilling exists precisely so no client derives this.
  // It is read here and nothing else is.
  const verdict = ipamResidueMeterVerdict({
    stillBilling,
    verified: billingVerified,
    configMessage: message,
  });
  const meterAsserted = verdict.asserted;
  const m = Number(totalAccounts);
  const scope = Number.isFinite(m) && m >= n && n > 0
    ? `${n} of ${m} accounts`
    : `${n} ${n === 1 ? 'account' : 'accounts'}`;

  return (
    <Alert
      severity={meterAsserted ? 'error' : 'warning'}
      icon={<ReportProblemOutlinedIcon fontSize="inherit" />}
      sx={{ mb: 3, fontFamily: FONT_BODY }}
      action={onRetry ? (
        <Button
          color="inherit"
          size="small"
          startIcon={<ReplayIcon />}
          disabled={busy}
          onClick={onRetry}
          sx={plainButtonSx}
        >
          Retry teardown
        </Button>
      ) : null}
    >
      <AlertTitle sx={{ fontFamily: FONT_TITLE, fontWeight: 600 }}>
        {verdict.title}
      </AlertTitle>
      <Typography sx={{ ...bodyTextSx, color: 'inherit' }}>
        {n > 0
          ? `Teardown failed for ${scope}: ${accounts.join(', ')}. `
          : 'Teardown did not finish. '}
        {verdict.line}
      </Typography>
      {n > 0 && (
        <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, mt: 1, mb: 0 }}>
          {accounts.map((id) => (
            <Typography component="li" key={id} sx={{ ...hintTextSx, color: 'inherit' }}>
              <Box component="span" sx={{ fontFamily: FONT_MONO }}>{id}</Box>
              {` — ${failures[id]}`}
            </Typography>
          ))}
        </Stack>
      )}
      {n === 0 && message && (
        <Typography sx={{ ...hintTextSx, color: 'inherit', mt: 1 }}>{message}</Typography>
      )}
      {/* No dispatch here means no dispatch error here: the host owns the
          confirmation and therefore owns the failure it can produce. */}
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------

/**
 * IpamBlockedPanel — what `config.status === 'blocked'` means, and what to do.
 *
 * `blocked` covers TWO job states, which is why this panel branches rather than
 * assuming blockers exist: a job at `blocked` is waiting on the customer's own
 * engineer to delete a resource, and a job at `awaiting_cascade` is waiting on an
 * operator's force-delete decision. Both park the config at `blocked`; only the
 * second may run the force delete.
 *
 * Props: { config, job, onRetried }. `job` is optional — pass it if the host page
 * already polls /teardown/active, and this panel polls for itself if not, so it
 * can be dropped into IpamEntry with only `config`.
 */
export default function IpamBlockedPanel({
  config,
  job: jobProp,
  onRetried,
  stillBilling,
  billingVerified,
  billingNote = '',
}) {
  const controlled = jobProp !== undefined;
  const [job, setJob] = useState(jobProp || null);
  const [jobError, setJobError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  // WAS THIS IPAM PRISM'S TO DELETE? `ipamAdopted` marks one that already existed
  // in the host account. Prism never deletes it, so every promise on this panel
  // about the meter stopping and the history going is false for it — and the
  // backend already says so in the pre-flight, in a `critical` row.
  const adopted = config?.ipamAdopted === true;

  useEffect(() => {
    if (controlled) setJob(jobProp || null);
  }, [controlled, jobProp]);

  // The interval is owned by the effect and keyed on primitives only. Keying it
  // on the job object would rebuild it on every tick, drifting the cadence and
  // resetting the failure counter each cycle — the bug this portal has already
  // shipped twice.
  useEffect(() => {
    if (controlled) return undefined;
    let cancelled = false;
    let failures = 0;
    let timer = null;
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const tick = async () => {
      try {
        const res = await getIpamActiveTeardown();
        if (cancelled) return;
        failures = 0;
        setJob(res?.data || null);
        setJobError('');
      } catch (e) {
        if (cancelled) return;
        failures += 1;
        if (failures >= MAX_POLL_FAILURES) {
          setJobError(e?.message || 'Prism could not read the live teardown job.');
          stop();
        }
      }
    };
    tick();
    timer = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      stop();
    };
  }, [controlled, reloadKey]);

  const blockers = useMemo(() => parseIpamBlockers(job?.blockersJson), [job]);
  const groups = useMemo(() => groupIpamBlockersByPool(blockers), [blockers]);

  const awaitingCascade = job?.status === IPAM_TEARDOWN_AWAITING_CASCADE;
  const cascadeReason = awaitingCascade
    ? ''
    : (jobError ? IPAM_CASCADE_UNREADABLE_REASON : IPAM_CASCADE_NOT_YET_REASON);

  // THE BACKEND'S OWN PROSE FOR THIS TEARDOWN, when it wrote any. On the new
  // deleted-and-out-of-budget arm it is the only thing on this page that knows
  // AWS confirmed the delete, so it is rendered rather than summarised. It is
  // skipped when it parses as the per-account failure map, which the residue
  // banner already renders properly keyed.
  const configMessage = useMemo(() => {
    const text = String(config?.errorMessage || '').trim();
    if (!text) return '';
    return parseIpamAccountMessage(text) ? '' : text;
  }, [config?.errorMessage]);

  // `blocked` USED TO IMPLY A BLOCKER AND NO LONGER DOES. The teardown's new
  // settle arm parks the job at `blocked` with an EMPTY blocker list to mean the
  // exact opposite of the old one — AWS confirmed the IPAM deleted, the meter
  // stopped, and the pass simply ran out of graceful budget before the member-side
  // cleanup. Rendering "these resources are blocking teardown" over zero rows, or
  // "still billing" over a stopped meter, are both statements this panel cannot
  // support, so the wording is chosen from the count and the cascade flag rather
  // than assumed from the status.
  const blockedCopy = useMemo(() => ipamTeardownBlockedCopy({
    blockerCount: blockers.length,
    awaitingCascade,
    configMessage,
  }), [blockers.length, awaitingCascade, configMessage]);

  const retry = async () => {
    setBusy(true);
    setActionError('');
    try {
      await disableIpam('graceful');
      setReloadKey((k) => k + 1);
      if (onRetried) onRetried();
    } catch (e) {
      setActionError(e?.message || 'Could not restart the VPC IPAM teardown.');
    } finally {
      setBusy(false);
    }
  };

  const forceDelete = async () => {
    setBusy(true);
    setActionError('');
    try {
      await cascadeDeleteIpam(true);
      // Closing on both outcomes is one of the dialog's exit paths, and the
      // dialog clears the typed customer ID on every one of them.
      setConfirmOpen(false);
      setReloadKey((k) => k + 1);
      if (onRetried) onRetried();
    } catch (e) {
      setConfirmOpen(false);
      setActionError(e?.message || 'Could not force delete the IPAM.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Alert
        severity={blockedCopy.blockersHold || awaitingCascade ? 'error' : 'warning'}
        sx={{ mb: 3, fontFamily: FONT_BODY }}
      >
        <AlertTitle sx={{ fontFamily: FONT_TITLE, fontWeight: 600 }}>
          {blockedCopy.title}
        </AlertTitle>
        <Typography sx={bodyTextSx}>{blockedCopy.body}</Typography>
        {configMessage && (
          <Typography sx={{ ...bodyTextSx, mt: 1, fontWeight: 600 }}>{configMessage}</Typography>
        )}
      </Alert>

      {actionError && <Alert severity="error" sx={{ mb: 3 }}>{actionError}</Alert>}

      {groups.length > 0 && (
        <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
          <Typography sx={{ ...sectionHeadingSx, mb: 2 }}>What is holding the teardown</Typography>
          <Stack spacing={2}>
            {groups.map((group) => (
              <Box key={group.poolId || 'unattributed'} sx={insetPanelSx}>
                <Typography sx={{ ...bodyTextSx, color: CK.navy, fontWeight: 600, mb: 1 }}>
                  Pool{' '}
                  <Box component="span" sx={{ fontFamily: FONT_MONO }}>
                    {group.poolId || 'unknown'}
                  </Box>
                  {group.cidrs.length > 0 && (
                    <>
                      {' ('}
                      <Box component="span" sx={{ fontFamily: FONT_MONO }}>
                        {group.cidrs.join(', ')}
                      </Box>
                      {')'}
                    </>
                  )}
                  {` still has ${group.items.length} `}
                  {group.items.length === 1 ? 'allocation' : 'allocations'}
                  {' from a live resource:'}
                </Typography>
                <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, my: 0 }}>
                  {group.items.map((b) => (
                    <Typography
                      component="li"
                      key={`${b.allocationId || ''}-${b.resourceId || ''}`}
                      sx={monoTextSx}
                    >
                      {blockerLine(b)}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {jobError && groups.length === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Prism could not read the teardown job, so it cannot name the blocking resources on this
          screen. Everything below is still true. ({jobError})
        </Alert>
      )}

      <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
        <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>What happens next</Typography>
        <Stack spacing={1.5}>
          {/* The standing promise holds in every arm. The INSTRUCTION under it
              does not: "Delete the VPC (or release its CIDR), wait for IPAM to
              catch up, then press Retry teardown" names work that does not exist
              when the blocker list is empty, and sends an operator hunting for a
              resource nothing is waiting on. */}
          <Typography sx={{ ...bodyTextSx, color: CK.navy, fontWeight: 600 }}>
            {IPAM_NEVER_DELETES_YOUR_RESOURCES}
          </Typography>
          {blockedCopy.blockersHold ? (
            <>
              <Typography sx={bodyTextSx}>{IPAM_ALLOCATION_RELEASE_DELAY}</Typography>
              <Typography sx={hintTextSx}>
                AWS refuses to release an allocation that belongs to a live resource and instructs
                you to remove the resource instead; the allocation then lingers for several minutes
                after the resource is gone, during which a release still refuses. That wait is
                normal, not a fault.
              </Typography>
            </>
          ) : (
            <Typography sx={bodyTextSx}>
              Nothing in your accounts has to change before this can be retried — Prism is not
              waiting on a resource. Press <b>Retry teardown</b> and it resumes from where it
              stopped.
            </Typography>
          )}
        </Stack>

        {/* THE METER, AND THE THREE THINGS THIS PANEL CAN SAY ABOUT IT. It used
            to say one: "still running and still billing until it is deleted",
            flatly, in every state — including the one AWS has confirmed deleted.
            `meterVerdict` is null when the backend wrote its own prose above,
            which is the better witness. */}
        {(adopted || blockedCopy.meterVerdict) && (
          <Alert severity="warning" sx={{ mt: 2, mb: 2, fontFamily: FONT_BODY }}>
            <Typography sx={{ ...bodyTextSx, color: 'inherit', fontWeight: 600 }}>
              {adopted ? IPAM_ADOPTED_TEARDOWN_SUMMARY : blockedCopy.meterVerdict}
            </Typography>
            <Typography sx={{ ...bodyTextSx, color: 'inherit' }}>
              Advanced-tier charges cover every active IP in the accounts and Regions you enrolled.{' '}
              {ipamBillingStops(adopted)}
            </Typography>
            {/* ── THE MISSING COMPANION, NOT A CORRECTION ──────────────────────
                NOTHING ABOVE THIS LINE MOVES, and that is deliberate.
                IPAM_ADOPTED_TEARDOWN_SUMMARY and ipamBillingStops are statements
                about what a teardown DOES to this IPAM — that it is not deleted,
                that its history survives, that the meter keeps running. They are
                true whatever the ledger says, they are pinned by tests, and
                hedging them would be hedging the wrong sentence.

                What was absent is any statement of what the meter IS. This panel
                was the last screen in the capability that never read the
                pre-flight verdict: the residue banner exported from THIS VERY
                FILE was given the confidence axis while the default export beside
                it was not, because the two are wired from different parents and
                only one parent was fetching the payload.

                It matters most exactly here. A config parked at `blocked` has no
                live ledger observation behind it, so this is the screen where an
                unverified verdict is the norm rather than the exception — and it
                is the screen an operator reaches while actively trying to stop
                the charge. Undefined stays the hedge: with no payload the copy
                falls to "Prism has not read the ledger, treat it as billing",
                which is the correct sentence for a panel that fetched nothing. */}
            <Typography sx={{ ...bodyTextSx, color: 'inherit', mt: 1 }}>
              {ipamPreviewBillingLine(stillBilling, billingVerified)}
            </Typography>
            {/* The backend's own reason, verbatim, when it sends one. The line
                above is basis-neutral BY DESIGN — `billingVerified === false`
                covers a sighting Prism has stopped refreshing and a ledger that
                never held one, and generic copy may presuppose neither — so this
                is the only channel through which the specific, dated claim can
                reach the operator. */}
            {String(billingNote || '').trim() && (
              <Typography sx={{ ...bodyTextSx, color: 'inherit', mt: 1 }}>
                {String(billingNote).trim()}
              </Typography>
            )}
          </Alert>
        )}

        <Box sx={actionRowSx}>
          <Button
            variant="contained"
            startIcon={<ReplayIcon />}
            disabled={busy}
            onClick={retry}
            sx={ipamButtonSx}
          >
            Retry teardown
          </Button>
          {job?.stage && (
            <Chip
              size="small"
              label={`Stage ${job.stage}${job.attempt ? ` · attempt ${job.attempt}` : ''}`}
              sx={statusChipSx('neutral')}
            />
          )}
        </Box>
      </Paper>

      <Divider sx={{ mb: 3 }} />

      {/* The escape hatch sits BELOW retry and never above it: it is the answer
          to "I cannot remove these resources", not the first thing to try. */}
      <Paper elevation={0} sx={sectionCardSx}>
        <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>
          Force delete — if you cannot remove those resources
        </Typography>
        <Typography sx={{ ...bodyTextSx, mb: 1.5 }}>{IPAM_FORCE_DELETE_COPY}</Typography>
        <Typography sx={{ ...bodyTextSx, color: CK.navy, fontWeight: 600, mb: 0.5 }}>
          {IPAM_NO_VPC_DELETED}
        </Typography>
        {/* Force delete is the ONE path that does delete an adopted IPAM — the
            operator asks for DeleteIpam --cascade by name — so this section keeps
            the destruction sentence whichever way `adopted` reads. What must not
            survive adoption is the ORDERED teardown's promise above, which deletes
            nothing and stops no meter. */}
        <Typography sx={{ ...bodyTextSx, color: CK.red, fontWeight: 600, mb: 1.5 }}>
          {IPAM_HISTORY_DESTROYED}
        </Typography>
        {adopted && (
          <Typography sx={{ ...bodyTextSx, color: CK.navy, mb: 1.5 }}>
            This IPAM is one Prism adopted. The ordered teardown leaves it running and metering;
            only this force delete removes it, and it removes it permanently.
          </Typography>
        )}

        {cascadeReason && (
          <Alert severity="info" sx={{ mb: 2 }}>{cascadeReason}</Alert>
        )}

        <Button
          color="error"
          variant="outlined"
          disabled={busy || !awaitingCascade || !config?.customerId}
          onClick={() => setConfirmOpen(true)}
          sx={plainButtonSx}
        >
          Force delete the IPAM
        </Button>
      </Paper>

      <IpamDestructiveConfirmDialog
        open={confirmOpen}
        customerId={config?.customerId}
        title="Confirm force delete"
        busy={busy}
        blockers={blockers}
        // NOT `adopted`. This dialog confirms DeleteIpam --cascade, which really
        // does delete the IPAM and really does destroy the history, adopted or
        // not — so it keeps the destruction wording deliberately.
        adopted={false}
        onClose={() => setConfirmOpen(false)}
        onConfirm={forceDelete}
        intro={(
          <Typography sx={bodyTextSx}>
            This runs <b>DeleteIpam --cascade</b> against the IPAM in account{' '}
            <b>{config?.hostAccountId || 'the host account'}</b>. It cannot be undone.
          </Typography>
        )}
      />
    </Box>
  );
}
